import { useState, useEffect } from "react";
import {
  Box, Grid, TextField, Button, Typography,
  IconButton, Checkbox, FormControlLabel, Divider,
  Chip, InputAdornment, Paper, Collapse, MenuItem,
  ToggleButton, ToggleButtonGroup, Autocomplete,
  List, ListItem, ListItemText
} from "@mui/material";
import CloseIcon              from "@mui/icons-material/Close";
import PersonIcon             from "@mui/icons-material/Person";
import CurrencyRupeeIcon      from "@mui/icons-material/CurrencyRupee";
import PaymentIcon            from "@mui/icons-material/Payment";
import SwapHorizIcon          from "@mui/icons-material/SwapHoriz";
import CardGiftcardIcon       from "@mui/icons-material/CardGiftcard";
import ReceiptIcon            from "@mui/icons-material/Receipt";
import AccountBalanceIcon     from "@mui/icons-material/AccountBalance";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PendingActionsIcon     from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteIcon             from "@mui/icons-material/Delete";
import { sellBikeService }    from "../service/SellBikeService";
import api                    from "../service/api";

const LOCATIONS = ["Khejuri", "Chaitanyapur", "Nandigram"];
const FINANCE   = [
  "bajaj auto credit ltd", "Tata capital","L & T FINANCE LTD.", "IDFC first bank","HDB financial services Ltd",
  "Shriram Finance Limited", "ujjivan small finance Bank Limited","HDFC BANK LTD."
];

// ─── localStorage key per bike ────────────────────────────────────────────────
// Each bike gets its own key so drafts don't mix across different sales.
const getDraftKey = (bikeId) => `sell_draft_${bikeId}`;

// ─── Section Header ───────────────────────────────────────────────────────────
const Section = ({ icon, title, color = "#1976d2" }) => (
  <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Typography
      variant="subtitle2" fontWeight={700} letterSpacing={1}
      sx={{ color, textTransform: "uppercase", fontSize: "0.72rem" }}
    >
      {title}
    </Typography>
    <Box flex={1} height="1px" bgcolor={`${color}33`} ml={1} />
  </Box>
);

// ─── Reusable Field ───────────────────────────────────────────────────────────
const F = ({
  label, name, value, onChange, error, helperText,
  type = "text", required, xs = 12, md = 6,
  startAdornment, multiline, rows, select, children
}) => (
  <Grid item xs={xs} md={md}>
    <TextField
      fullWidth size="small" label={label} name={name}
      value={value} onChange={onChange} type={type}
      required={required} error={!!error} helperText={error || helperText}
      multiline={multiline} rows={rows} select={select}
      InputProps={startAdornment ? {
        startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment>
      } : undefined}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
    >
      {select && children}
    </TextField>
  </Grid>
);

const qtyIcon = <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />;
const rupee   = <CurrencyRupeeIcon sx={{ fontSize: 14 }} />;

// ─── Main Component ───────────────────────────────────────────────────────────
const SellBikeForm = ({ bike, onClose, onSuccess }) => {

  const today    = new Date().toISOString().split("T")[0];
  const draftKey = getDraftKey(bike?._id);

  // ── CHANGE 1: build initial state from localStorage if available ───────────
    const getInitialForm = () => {
  try {
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...parsed,
        giftsQty: parsed.giftsQty ?? "0",
        giftsList: parsed.giftsList ?? [],
        otherExpenses: parsed.otherExpenses ?? "",
                dueNote: parsed.dueNote ?? ""

      };
    }
  } catch {
    // corrupt data — ignore and use defaults
  }
  return {
    bikeId: bike?._id,
    customerName: "", fatherName: "", mobile: "", mobile2: "",
    address: "", village: "", postOffice: "", policeStation: "",
    pinCode: "", landmark: "",
    salePrice: "", orp: "", GSTIN: "",regNo:"", saleDate: today,
    isCash: true, isFinance: false, isExchange: false,
    cashAmount: "",DP:"",isDP:true,
    financeCompany: "", financeAmount: "",
    exchange: {
      ownerName: "", location: "", model: "",
      registrationNumber: "", exchangePrice: "", phone: ""
    },
    helmet: "", seatCover: "", bikeCover: "", transport: "",petrol:"",legguard:"",
    broker: "", tcr: "", regBroker: "", amcRsa: "", otherExpenses: "",
    gifts: "", giftsQty: "0", giftsList: [],
    rto: "", insurance: "",
    discount: "", couponCashback: "",
    dueStatus: "PAID", dueTotalAmount: "", dueNote: "",
    salesmanName: "", salesmanIncentive: "",
    location: bike?.location || "",
    remarks: ""
  };
};

  const [form, setForm]             = useState(getInitialForm);
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [hasDraft, setHasDraft]     = useState(false);

  // 🎁 NEW: known gift names, fetched once, used to power the autocomplete below
  const [giftOptions, setGiftOptions] = useState([]);

  useEffect(() => {
    let active = true;
    api.get("/accessories")
      .then((res) => {
        if (!active) return;
        const rows = res.data?.data || res.data || [];
        const names = rows
          .filter((a) => a.itemType === "Gift" && a.giftName)
          .map((a) => a.giftName);
        setGiftOptions([...new Set(names)]);
      })
      .catch(() => { /* ignore — free text still works without suggestions */ });
    return () => { active = false; };
  }, []);

  // ── CHANGE 2: check if a saved draft exists (to show restore banner) ───────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) setHasDraft(true);
    } catch { /* ignore */ }
  }, [draftKey]);

  // ── CHANGE 3: save to localStorage on every form change ───────────────────
  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify(form));
    } catch { /* storage full — ignore */ }
  }, [form, draftKey]);

  // ── Derived display values ─────────────────────────────────────────────────
  const orpNum     = Number(form.orp            || 0);
  const discNum    = Number(form.discount       || 0);
  const couponNum  = Number(form.couponCashback || 0);
  const netDisplay = orpNum - discNum - couponNum;

  const dueRemaining =
    form.dueStatus === "DUE" && form.dueTotalAmount
      ? Number(form.dueTotalAmount)
      : null;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    if (errors[e.target.name])
      setErrors((p) => { const n = { ...p }; delete n[e.target.name]; return n; });
  };

  const handleExchangeChange = (e) => {
    setForm((p) => ({ ...p, exchange: { ...p.exchange, [e.target.name]: e.target.value } }));
    if (errors[e.target.name])
      setErrors((p) => { const n = { ...p }; delete n[e.target.name]; return n; });
  };

  const toggle = (key) => setForm((p) => ({ ...p, [key]: !p[key] }));

  const handleDueStatus = (_, val) => {
    if (val) setForm((p) => ({ ...p, dueStatus: val, dueTotalAmount: "", dueNote: "" }));
  };

  // 🎁 add current gift entry (gifts + giftsQty) to giftsList array
  const handleAddGift = () => {
    if (!form.gifts) return;
    setForm((p) => ({
      ...p,
      giftsList: [
        ...p.giftsList,
        { name: p.gifts, quantity: Number(p.giftsQty) || 0 }
      ],
      gifts: "",
      giftsQty: "0"
    }));
  };

  // 🎁 remove a gift from giftsList by index
  const handleRemoveGift = (index) => {
    setForm((p) => ({
      ...p,
      giftsList: p.giftsList.filter((_, i) => i !== index)
    }));
  };

  // ── Clear draft manually ──────────────────────────────────────────────────
  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    setHasDraft(false);
    setForm(getInitialForm());
  };

  // ── Validate ──────────────────────────────────────────────────────────────
  const validate = () => {
    const temp = {};
    if (!form.customerName.trim()) temp.customerName = "Customer name is required";
    if (!form.mobile.trim())       temp.mobile       = "Mobile number is required";
    if (!form.salePrice)           temp.salePrice    = "Sale price is required";
    if (!form.orp)                 temp.orp          = "ORP is required";
    if (!form.saleDate)            temp.saleDate     = "Sale date is required";
    if (!form.location)            temp.location     = "Location is required";

    if (form.isCash && !form.cashAmount)
      temp.cashAmount = "Cash amount is required";
if (form.isDP && !form.DP)
      temp.DP = "DP amount is required";
    if (form.isFinance) {
      if (!form.financeCompany.trim()) temp.financeCompany = "Finance company is required";
      if (!form.financeAmount)         temp.financeAmount  = "Finance amount is required";
    }

    if (form.isExchange) {
      if (!form.exchange.ownerName.trim())
        temp.ownerName          = "Owner name is required";
      if (!form.exchange.registrationNumber.trim())
        temp.registrationNumber = "Registration number is required";
      if (!form.exchange.exchangePrice)
        temp.exchangePrice      = "Exchange price is required";
    }

    if (form.dueStatus === "DUE" && !form.dueTotalAmount)
      temp.dueTotalAmount = "Total due amount is required";

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    const mobiles = [];
    if (form.mobile)  mobiles.push(String(form.mobile).trim());
    if (form.mobile2) mobiles.push(String(form.mobile2).trim());

    const salePrice   = Number(form.salePrice               || 0);
    const orp         = Number(form.orp                     || 0);
    const cash        = Number(form.cashAmount              || 0);
    const DP        = Number(form.DP              || 0);
    const finance     = Number(form.financeAmount           || 0);
    const exchangeAmt = Number(form.exchange?.exchangePrice || 0);

    try {
      const payload = {
        bikeId: form.bikeId,
        customerName: form.customerName, fatherName: form.fatherName,
        mobile: mobiles, address: form.address, village: form.village,
        postOffice: form.postOffice, policeStation: form.policeStation,
        pinCode: form.pinCode, landmark: form.landmark,
        salePrice, orp, GSTIN: form.GSTIN, regNo:form.regNo, saleDate: form.saleDate,
        saleType: form.isExchange ? "EXCHANGE" : form.isFinance ? "FINANCE" : "CASH",
        cashAmount: form.isCash ? cash : 0, DP:form.isDP?DP:0,
        finance: form.isFinance
          ? { companyName: form.financeCompany, amount: finance }
          : undefined,
        exchange: form.isExchange
          ? {
              ownerName: form.exchange.ownerName,
              location:  form.exchange.location,
              model:     form.exchange.model,
              registrationNumber: form.exchange.registrationNumber,
              exchangePrice: exchangeAmt,
              phone: form.exchange.phone
            }
          : undefined,
        expenses: {
          helmet:    Number(form.helmet    || 0),
          seatCover: Number(form.seatCover || 0),
          bikeCover: Number(form.bikeCover || 0),
          transport: Number(form.transport || 0),
          petrol: Number(form.petrol || 0),
          legguard: Number(form.legguard || 0),
          broker:    Number(form.broker    || 0),
          tcr:       Number(form.tcr       || 0),
          regBroker: Number(form.regBroker || 0),
          amcRsa:    Number(form.amcRsa    || 0),
          otherExpenses: Number(form.otherExpenses || 0)
        },
        gifts: form.giftsList,
        charges: {
          rto:       Number(form.rto       || 0),
          insurance: Number(form.insurance || 0)
        },
        discount:       Number(form.discount       || 0),
        couponCashback: Number(form.couponCashback || 0),
        dueAmount: form.dueStatus === "DUE" ? Number(form.dueTotalAmount) : 0,
         dueNote:   form.dueStatus === "DUE" ? form.dueNote : "",
        salesman: {
          name:      form.salesmanName,
          incentive: Number(form.salesmanIncentive || 0)
        },
        location: form.location,
        remarks:  form.remarks
      };

      await sellBikeService.sellBike(payload);

      // ── CHANGE 4: clear draft on success ──────────────────────────────────
      localStorage.removeItem(draftKey);

      if (typeof onSuccess === "function") onSuccess();
      onClose();

    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error   ||
        err.message                 ||
        "Something went wrong. Please try again.";
      setErrors((p) => ({ ...p, api: message }));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      p={3}
      position="relative"
      sx={{
        maxHeight: "90vh", overflowY: "auto",
        background: "linear-gradient(145deg, #f8faff 0%, #fff 100%)",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { borderRadius: 2, bgcolor: "#c5d5ea" }
      }}
    >
      {/* ── HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0d1b2a">Sell Bike</Typography>
          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
            {bike?.model   && <Chip label={bike.model}            size="small" color="primary" variant="outlined" />}
            {bike?.engine  && <Chip label={`Eng: ${bike.engine}`} size="small" variant="outlined" />}
            {bike?.skuCode && <Chip label={bike.skuCode}          size="small" variant="outlined" />}
            {bike?.colour  && <Chip label={bike.colour}           size="small" sx={{ bgcolor: "#fff3e0" }} />}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* ── CHANGE 5: draft restore banner ── */}
      {hasDraft && (
        <Paper variant="outlined" sx={{
          px: 2, py: 1, mb: 1.5, borderRadius: 1.5,
          bgcolor: "#fff8e1", borderColor: "#ffc107",
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Typography variant="caption" fontWeight={600} color="#e65100">
            📋 Draft restored — your previously entered data has been loaded.
          </Typography>
          <Button size="small" color="error" onClick={clearDraft}
            sx={{ textTransform: "none", fontSize: 11, py: 0 }}>
            Clear Draft
          </Button>
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={1.5}>

        {/* ── CUSTOMER ── */}
        <Grid item xs={12}>
          <Section icon={<PersonIcon fontSize="small" />} title="Customer Details" color="#1565c0" />
        </Grid>
        <F label="Customer Name *" name="customerName" value={form.customerName}
          onChange={handleChange} error={errors.customerName} required md={6} />
        <F label="Father Name (C/O)" name="fatherName" value={form.fatherName}
          onChange={handleChange} md={6} />
        <F label="Mobile *" name="mobile" value={form.mobile}
          onChange={handleChange} error={errors.mobile} required type="tel" md={6} />
        <F label="Alternate Mobile" name="mobile2" value={form.mobile2}
          onChange={handleChange} type="tel" md={6} />
        <F label="Address" name="address" value={form.address}
          onChange={handleChange} xs={12} md={12} />
        <F label="Village"        name="village"       value={form.village}       onChange={handleChange} md={4} />
        <F label="Post Office"    name="postOffice"    value={form.postOffice}    onChange={handleChange} md={4} />
        <F label="Police Station" name="policeStation" value={form.policeStation} onChange={handleChange} md={4} />
        <F label="PIN Code"       name="pinCode"       value={form.pinCode}       onChange={handleChange} type="number" md={4} />
        <F label="Landmark"       name="landmark"      value={form.landmark}      onChange={handleChange} md={8} />

        {/* ── SALE DETAILS ── */}
        <Grid item xs={12}>
          <Section icon={<CurrencyRupeeIcon fontSize="small" />} title="Sale Details" color="#2e7d32" />
        </Grid>
        <F label="ExShowroom Price *" name="salePrice" value={form.salePrice}
          onChange={handleChange} error={errors.salePrice} required type="number"
          startAdornment={rupee} md={4} />
        <F label="ORP *" name="orp" value={form.orp}
          onChange={handleChange} error={errors.orp} required type="number"
          startAdornment={rupee} md={4} />
        <F label="GSTIN" name="GSTIN" value={form.GSTIN}
          onChange={handleChange} md={4} />   
           <F label="Reg. No" name="regNo" value={form.regNo}
          onChange={handleChange} md={4} />
           
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth size="small" label="Sale Date *" name="saleDate"
            type="date" value={form.saleDate} onChange={handleChange}
            error={!!errors.saleDate} helperText={errors.saleDate}
            required InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>
        <F label="Location *" name="location" value={form.location}
          onChange={handleChange} error={errors.location} required select md={4}>
          {LOCATIONS.map((loc) => (
            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
          ))}
        </F>

        {/* ── PAYMENT MODE ── */}
        <Grid item xs={12}>
          <Section icon={<PaymentIcon fontSize="small" />} title="Payment Mode" color="#6a1b9a" />
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControlLabel
              control={<Checkbox checked={form.isCash} onChange={() => toggle("isCash")} size="small" sx={{ color: "#2e7d32" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#2e7d32">CASH</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={form.isFinance} onChange={() => toggle("isFinance")} size="small" sx={{ color: "#1565c0" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#1565c0">FINANCE</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={form.isExchange} onChange={() => toggle("isExchange")} size="small" sx={{ color: "#e65100" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#e65100">EXCHANGE</Typography>}
            />
          </Paper>
        </Grid>

        {/* CASH */}
        <Collapse in={form.isCash} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <F label="Cash Amount *" name="cashAmount" value={form.cashAmount}
              onChange={handleChange} error={errors.cashAmount} required type="number"
              startAdornment={rupee} md={6} />
              
                    <F label="DP Amount *" name="DP" value={form.DP}
              onChange={handleChange} error={errors.DP} required type="number"
              startAdornment={rupee} md={6} />

          </Grid>
        </Collapse>

        {/* FINANCE */}
        <Collapse in={form.isFinance} style={{ width: "100%" }}>
          <Grid item xs={12}>
            <Section icon={<AccountBalanceIcon fontSize="small" />} title="Finance Details" color="#1565c0" />
          </Grid>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <F label="Finance Company *" name="financeCompany" value={form.financeCompany}
              onChange={handleChange} error={errors.financeCompany} select md={6}>
              {FINANCE.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </F>
            <F label="Finance Amount *" name="financeAmount" value={form.financeAmount}
              onChange={handleChange} error={errors.financeAmount} required type="number"
              startAdornment={rupee} md={6} />
          </Grid>
        </Collapse>

        {/* EXCHANGE */}
        <Collapse in={form.isExchange} style={{ width: "100%" }}>
          <Grid item xs={12}>
            <Section icon={<SwapHorizIcon fontSize="small" />} title="Exchange Vehicle" color="#e65100" />
          </Grid>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <F label="Owner Name *" name="ownerName" value={form.exchange.ownerName}
              onChange={handleExchangeChange} error={errors.ownerName} required md={6} />
            <F label="Phone" name="phone" value={form.exchange.phone}
              onChange={handleExchangeChange} type="tel" md={6} />
            <F label="Model" name="model" value={form.exchange.model}
              onChange={handleExchangeChange} md={6} />
            <F label="Registration No *" name="registrationNumber" value={form.exchange.registrationNumber}
              onChange={handleExchangeChange} error={errors.registrationNumber} required md={6} />
            <F label="Exchange Price *" name="exchangePrice" value={form.exchange.exchangePrice}
              onChange={handleExchangeChange} error={errors.exchangePrice} required type="number"
              startAdornment={rupee} md={6} />
            <F label="Location" name="location" value={form.exchange.location}
              onChange={handleExchangeChange} select md={4}>
              {LOCATIONS.map((loc) => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </F>
          </Grid>
        </Collapse>

        {/* ── EXPENSES ── */}
        <Grid item xs={12}>
          <Section icon={<CardGiftcardIcon fontSize="small" />} title="Expenses / Freebies" color="#00838f" />
        </Grid>
        <F label="Helmet (qty)"     name="helmet"    value={form.helmet}    onChange={handleChange} type="number" md={4} startAdornment={qtyIcon} />
        <F label="Seat Cover (qty)" name="seatCover" value={form.seatCover} onChange={handleChange} type="number" md={4} startAdornment={qtyIcon} />
        <F label="Bike Cover (qty)" name="bikeCover" value={form.bikeCover} onChange={handleChange} type="number" md={4} startAdornment={qtyIcon} />
        <F label="Petrol(₹)"   name="petrol"  value={form.petrol} onChange={handleChange} type="number" md={4} startAdornment={rupee} />
       <F label="Leg Guard(₹)"   name="legguard"  value={form.legguard} onChange={handleChange} type="number" md={4} startAdornment={rupee} />
        <F label="Transport (₹)"   name="transport"  value={form.transport} onChange={handleChange} type="number" md={4} startAdornment={rupee} />
        <F label="Broker (₹)"      name="broker"     value={form.broker}    onChange={handleChange} type="number" md={4} startAdornment={rupee} />
        <F label="TCR (₹)"         name="tcr"        value={form.tcr}       onChange={handleChange} type="number" md={4} startAdornment={rupee} />
        <F label="Reg. Broker (₹)" name="regBroker"  value={form.regBroker} onChange={handleChange} type="number" md={4} startAdornment={rupee} />
        <F label="AMC / RSA (₹)"   name="amcRsa"     value={form.amcRsa}    onChange={handleChange} type="number" md={4} startAdornment={rupee} />
        <F label="Other Expenses (₹)" name="otherExpenses" value={form.otherExpenses} onChange={handleChange} type="number" md={4} startAdornment={rupee} />

        {/* ── GIFTS ── */}
        <Grid item xs={12}>
          <Section icon={<CardGiftcardIcon fontSize="small" />} title="Gifts" color="#ad1457" />
        </Grid>
        {/* 🎁 CHANGED: autocomplete suggests every gift name used before, freeSolo still allows a brand-new one */}
        <Grid item xs={12} md={7}>
          <Autocomplete
            freeSolo
            size="small"
            options={giftOptions}
            value={form.gifts}
            onInputChange={(_, newValue) => {
              setForm((p) => ({ ...p, gifts: newValue }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gift Item Name"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            )}
          />
        </Grid>
        <F label="Qty" name="giftsQty" value={form.giftsQty} onChange={handleChange} type="number" md={3} />
        <Grid item xs={12} md={2}>
          <Button variant="outlined" fullWidth sx={{ height: "40px" }} onClick={handleAddGift}>
            Add
          </Button>
        </Grid>

        {form.giftsList.length > 0 && (
          <Grid item xs={12}>
            <List dense sx={{ bgcolor: "#fdf2f6", borderRadius: 1.5 }}>
              {form.giftsList.map((g, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleRemoveGift(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={`${g.name} × ${g.quantity}`} />
                </ListItem>
              ))}
            </List>
          </Grid>
        )}

        {/* ── CHARGES ── */}
        <Grid item xs={12}>
          <Section icon={<ReceiptIcon fontSize="small" />} title="Charges" color="#4527a0" />
        </Grid>
        <F label="RTO (₹)"       name="rto"       value={form.rto}       onChange={handleChange} type="number" startAdornment={rupee} md={6} />
        <F label="Insurance (₹)" name="insurance" value={form.insurance} onChange={handleChange} type="number" startAdornment={rupee} md={6} />

        {/* ── PRICING ── */}
        <Grid item xs={12}>
          <Section icon={<CurrencyRupeeIcon fontSize="small" />} title="Pricing" color="#558b2f" />
        </Grid>
        <F label="Discount (₹)"        name="discount"       value={form.discount}       onChange={handleChange} type="number" startAdornment={rupee} md={6} />
        <F label="Coupon Cashback (₹)" name="couponCashback" value={form.couponCashback} onChange={handleChange} type="number" startAdornment={rupee} md={6} />
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{
            px: 2, py: "9px", borderRadius: 1.5, height: "40px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            bgcolor: netDisplay < 0 ? "#fff5f5" : "#f1f8e9",
            borderColor: netDisplay < 0 ? "#ef9a9a" : "#a5d6a7"
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Net Amount (ORP − Discount − Coupon)
            </Typography>
            <Typography variant="body2" fontWeight={700} color={netDisplay < 0 ? "error.main" : "#2e7d32"}>
              ₹ {netDisplay.toLocaleString("en-IN")}
            </Typography>
          </Paper>
        </Grid>

        {/* ── PAYMENT STATUS ── */}
        <Grid item xs={12}>
          <Section icon={<PendingActionsIcon fontSize="small" />} title="Payment Status" color="#e65100" />
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{
            px: 2, py: 1.5, borderRadius: 2,
            display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
            bgcolor: form.dueStatus === "DUE" ? "#fff8f5" : "#f1f8e9",
            borderColor: form.dueStatus === "DUE" ? "#ff7043" : "#66bb6a"
          }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">Status:</Typography>
            <ToggleButtonGroup
              value={form.dueStatus} exclusive onChange={handleDueStatus} size="small"
              sx={{ "& .MuiToggleButton-root": { px: 2.5, py: 0.5, fontWeight: 700, fontSize: "0.75rem", borderRadius: "6px !important", border: "1px solid", mx: 0.5 } }}
            >
              <ToggleButton value="PAID" sx={{ borderColor: "#66bb6a !important", "&.Mui-selected": { bgcolor: "#2e7d32 !important", color: "#fff" } }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 15, mr: 0.5 }} /> PAID
              </ToggleButton>
              <ToggleButton value="DUE" sx={{ borderColor: "#ff7043 !important", "&.Mui-selected": { bgcolor: "#e65100 !important", color: "#fff" } }}>
                <PendingActionsIcon sx={{ fontSize: 15, mr: 0.5 }} /> DUE
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Grid>

        <Collapse in={form.dueStatus === "DUE"} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0.5 }}>
            <F label="Total Due Amount *" name="dueTotalAmount" value={form.dueTotalAmount}
              onChange={handleChange} error={errors.dueTotalAmount} required
              type="number" startAdornment={rupee} md={6}
              helperText="Amount customer still owes" />
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{
                px: 2, py: "9px", borderRadius: 1.5, height: "40px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                bgcolor: "#fff3e0", borderColor: "#ffb74d"
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Remaining at Sale Time
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#e65100">
                  ₹ {dueRemaining !== null ? dueRemaining.toLocaleString("en-IN") : "—"}
                </Typography>
              </Paper>
            </Grid>
            <F label="Note (optional)" name="dueNote" value={form.dueNote}
              onChange={handleChange} xs={12} md={12} multiline rows={2}
              helperText="e.g. Customer will pay remaining ₹5000 after RTO" />
          </Grid>
        </Collapse>

        {/* ── SALESMAN ── */}
        <Grid item xs={12}>
          <Section icon={<PersonIcon fontSize="small" />} title="Salesman" color="#795548" />
        </Grid>
        <F label="Salesman Name"  name="salesmanName"      value={form.salesmanName}      onChange={handleChange} md={6} />
        <F label="Incentive (₹)" name="salesmanIncentive" value={form.salesmanIncentive} onChange={handleChange} type="number" startAdornment={rupee} md={6} />

        {/* ── REMARKS ── */}
        <Grid item xs={12}>
          <Section icon={<ReceiptIcon fontSize="small" />} title="Remarks" color="#546e7a" />
        </Grid>
        <F label="Remarks" name="remarks" value={form.remarks}
          onChange={handleChange} xs={12} md={12} multiline rows={2} />

        {/* API ERROR */}
        {errors.api && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ px: 2, py: 1.2, borderRadius: 1.5, bgcolor: "#fff5f5", borderColor: "#f44336" }}>
              <Typography color="error" variant="body2" fontWeight={600}>
                ⚠️ {errors.api}
              </Typography>
            </Paper>
          </Grid>
        )}

        {Object.keys(errors).filter(k => k !== "api").length > 0 && (
          <Grid item xs={12}>
            <Typography color="error" variant="caption" textAlign="center" display="block">
              Please fix {Object.keys(errors).filter(k => k !== "api").length} error(s) before submitting.
            </Typography>
          </Grid>
        )}

      </Grid>

      <Button
        fullWidth variant="contained" onClick={handleSubmit} disabled={submitting}
        sx={{
          mt: 3, py: 1.3, borderRadius: 2, fontWeight: 700, fontSize: "0.95rem", letterSpacing: 1,
          background: submitting ? "#bdbdbd" : "linear-gradient(135deg, #1565c0, #42a5f5)",
          boxShadow: "0 4px 14px rgba(21,101,192,0.35)",
          "&:hover": { background: submitting ? "#bdbdbd" : "linear-gradient(135deg, #0d47a1, #1976d2)" }
        }}
      >
        {submitting ? "Saving..." : "✅ Confirm Sale"}
      </Button>

    </Box>
  );
};

export default SellBikeForm;